import { useCallback, useEffect, useRef, useState } from 'react';
import { useFetchWrapper } from '../_helpers/fetchWrapper.js';
import { isSignedIn } from '../_helpers/safeRedirect.js';
import config from '../config.js';

/**
 * The signed-in listener's favorite songs for one band (ENG-443, the listener
 * side of ENG-264), read from `GET /bands/{id}/favorites` and toggled through
 * `POST /bands/{id}/songs/{songID}/favorite` (ENG-162).
 *
 * `toggle(songId)` flips the star at once and rolls it back if the request
 * fails, setting `error` to `{ songId }` so the caller can say so. The POST
 * is itself a toggle; its answer (`favorited`) is the server's state, which
 * wins over the optimistic guess. A second toggle of a song whose first is
 * still out is ignored, so two quick clicks can't cancel each other on the
 * server while the star shows the opposite.
 *
 * Signed out, nothing is read and `toggle` returns 'signin' without a request:
 * the caller prompts for sign-in. Only the favorite set changes on a toggle,
 * never the song list, so a list keeps its order (ENG-264: a new order
 * appears on refresh or when Magic is chosen again).
 *
 * `status` is 'idle' (signed out), 'loading', 'ready' or 'error'.
 */
export const useFavorites = (bandId) => {
  const fetchWrapperRef = useRef(useFetchWrapper());
  const signedIn = isSignedIn();
  const [ids, setIds] = useState(() => new Set());
  const idsRef = useRef(ids);
  const [status, setStatus] = useState(signedIn && bandId ? 'loading' : 'idle');
  const [pending, setPending] = useState(() => new Set());
  const pendingRef = useRef(new Set());
  const [error, setError] = useState(null);

  const apply = useCallback((id, favorited) => {
    const next = new Set(idsRef.current);
    if (favorited) next.add(id);
    else next.delete(id);
    idsRef.current = next;
    setIds(next);
  }, []);

  useEffect(() => {
    if (!signedIn || !bandId) {
      setStatus('idle');
      return undefined;
    }
    let live = true;
    setStatus('loading');
    // Inside a promise, so a wrapper that throws outright is a failed load too.
    Promise.resolve()
      .then(() => fetchWrapperRef.current.get(`${config.apiBaseUrl}/bands/${bandId}/favorites`))
      .then((answer) => {
        if (!live) return;
        if (answer?.success === false) {
          setStatus('error');
          return;
        }
        const songIds = Array.isArray(answer?.song_ids) ? answer.song_ids : [];
        // A toggle made while the list was loading keeps its own state.
        const loaded = new Set(songIds.map(Number));
        pendingRef.current.forEach((id) => {
          if (idsRef.current.has(id)) loaded.add(id);
          else loaded.delete(id);
        });
        idsRef.current = loaded;
        setIds(loaded);
        setStatus('ready');
      })
      .catch(() => {
        if (live) setStatus('error');
      });
    return () => {
      live = false;
    };
  }, [bandId, signedIn]);

  const toggle = useCallback(async (songId) => {
    if (!isSignedIn()) return 'signin';
    const id = Number(songId);
    if (pendingRef.current.has(id)) return 'pending';
    pendingRef.current.add(id);
    setPending(new Set(pendingRef.current));
    const was = idsRef.current.has(id);
    apply(id, !was);
    setError(null);
    try {
      const answer = await fetchWrapperRef.current.post(`${config.apiBaseUrl}/bands/${bandId}/songs/${id}/favorite`);
      if (answer?.success === false) throw new Error(answer.error || 'Failed to toggle favorite');
      if (typeof answer?.favorited === 'boolean') apply(id, answer.favorited);
      return 'ok';
    } catch {
      apply(id, was);
      setError({ songId: id });
      return 'failed';
    } finally {
      pendingRef.current.delete(id);
      setPending(new Set(pendingRef.current));
    }
  }, [bandId, apply]);

  const isFavorite = useCallback((songId) => ids.has(Number(songId)), [ids]);
  const isPending = useCallback((songId) => pending.has(Number(songId)), [pending]);
  const clearError = useCallback(() => setError(null), []);

  return { signedIn, status, ids, isFavorite, isPending, toggle, error, clearError };
};
